import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, ImageSquare, ChatCircle, Heart } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { fileUrl } from "@/lib/api";
import PostCard from "@/components/PostCard";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { toast } from "sonner";

const FALLBACK_BANNER = "https://images.unsplash.com/photo-1698614083129-15e976a503fe?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800";

export default function CreatorProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    api.get(`/users/${username}`).then(({ data }) => setProfile(data)).catch(() => setNotFound(true));
    api.get(`/users/${username}/posts`).then(({ data }) => setPosts(data)).catch(() => {});
  }, [username]);

  useEffect(() => { load(); }, [load]);

  const onCheckout = (post) => {
    if (!user) return navigate("/login");
    if (post.visibility === "ppv") startCheckout({ kind: "ppv", resource_id: post.id });
    else startCheckout({ kind: "subscription", resource_id: profile.id });
  };

  const subscribe = () => {
    if (!user) return navigate("/login");
    startCheckout({ kind: "subscription", resource_id: profile.id });
  };

  const tip = (tier) => {
    if (!user) return navigate("/login");
    startCheckout({ kind: "tip", resource_id: profile.id, tip_tier: tier });
  };

  const toggleFollow = async () => {
    if (!user) return navigate("/login");
    try {
      if (profile.is_following) { await api.delete(`/users/${profile.id}/follow`); }
      else { await api.post(`/users/${profile.id}/follow`); }
      setProfile({ ...profile, is_following: !profile.is_following });
    } catch { toast.error("Something went wrong"); }
  };

  if (notFound) return <div className="py-32 text-center font-display text-4xl">Creator not found</div>;
  if (!profile) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  const banner = profile.banner_id ? fileUrl(profile.banner_id) : FALLBACK_BANNER;
  const audioPosts = posts.filter((p) => p.post_type === "audio");
  const mediaPosts = posts.filter((p) => p.post_type !== "audio");

  return (
    <div data-testid="creator-profile">
      {/* Banner */}
      <div className="relative h-56 md:h-80 overflow-hidden">
        <img src={banner} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto px-5 -mt-16 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end gap-5">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}
            className="h-32 w-32 rounded-full border-4 border-background bg-card overflow-hidden flex items-center justify-center shrink-0"
          >
            {profile.avatar_id ? <img src={fileUrl(profile.avatar_id)} alt="" className="h-full w-full object-cover" />
              : <span className="font-display text-5xl">{profile.display_name[0]}</span>}
          </motion.div>
          <div className="flex-1 pb-2">
            <h1 className="font-display text-4xl md:text-5xl leading-none">{profile.display_name}</h1>
            <p className="text-muted-foreground">@{profile.username}</p>
          </div>
          {!profile.is_self && (
            <div className="flex gap-2 pb-2">
              <button data-testid="follow-btn" onClick={toggleFollow}
                className={`px-5 py-3 font-accent font-bold uppercase tracking-widest text-xs border transition-colors ${profile.is_following ? "border-border text-muted-foreground hover:text-foreground" : "border-foreground hover:bg-foreground hover:text-background"}`}>
                {profile.is_following ? "Following" : "Follow"}
              </button>
              {profile.is_subscribed ? (
                <span data-testid="subscribed-badge" className="px-5 py-3 bg-primary/20 border border-primary text-primary font-accent font-bold uppercase tracking-widest text-xs">Subscribed</span>
              ) : profile.subscription_price > 0 && (
                <button data-testid="subscribe-btn" onClick={subscribe}
                  className="px-5 py-3 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-xs transition-transform active:scale-95 hover:bg-[#e6004c]">
                  Subscribe ${profile.subscription_price}/mo
                </button>
              )}
            </div>
          )}
        </div>

        {profile.bio && <p className="mt-6 text-foreground/90 leading-relaxed max-w-2xl">{profile.bio}</p>}

        <div className="flex flex-wrap gap-2 mt-4">
          {(profile.categories || []).map((c) => (
            <span key={c} className="px-3 py-1 border border-border text-xs font-accent uppercase tracking-wider text-muted-foreground">{c}</span>
          ))}
        </div>

        <div className="flex items-center gap-6 mt-6 text-sm text-muted-foreground font-accent">
          <span className="flex items-center gap-1.5"><Users size={16} weight="fill" className="text-primary" />{profile.stats?.subscribers} subs</span>
          <span className="flex items-center gap-1.5"><ImageSquare size={16} weight="fill" className="text-primary" />{profile.stats?.posts} posts</span>
          <span className="flex items-center gap-1.5"><Heart size={16} weight="fill" className="text-primary" />{profile.stats?.followers} followers</span>
          {!profile.is_self && (
            <button data-testid="message-btn" onClick={() => user ? navigate(`/messages/${profile.id}`) : navigate("/login")}
              className="ml-auto flex items-center gap-1.5 text-foreground hover:text-primary transition-colors">
              <ChatCircle size={16} /> Message
            </button>
          )}
        </div>

        {/* tips */}
        {!profile.is_self && (
          <div className="mt-6 p-4 bg-card border border-border">
            <p className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-3">Send a tip</p>
            <div className="flex gap-2">
              {[["tip_5", 5], ["tip_10", 10], ["tip_25", 25], ["tip_50", 50]].map(([tier, amt]) => (
                <button key={tier} data-testid={`tip-${tier}`} onClick={() => tip(tier)}
                  className="flex-1 py-2.5 border border-border font-accent font-bold text-sm hover:border-primary hover:text-primary transition-colors">
                  ${amt}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* content tabs */}
      <div className="max-w-2xl mx-auto px-5 py-10">
        <Tabs defaultValue="all">
          <TabsList className="bg-card border border-border w-full justify-start mb-6">
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
            <TabsTrigger value="audio" data-testid="tab-audio">Audio</TabsTrigger>
          </TabsList>
          <TabsContent value="all">
            <PostList posts={posts} onCheckout={onCheckout} />
          </TabsContent>
          <TabsContent value="media">
            <PostList posts={mediaPosts} onCheckout={onCheckout} />
          </TabsContent>
          <TabsContent value="audio">
            <PostList posts={audioPosts} onCheckout={onCheckout} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const PostList = ({ posts, onCheckout }) =>
  posts.length ? (
    <div className="flex flex-col gap-6">
      {posts.map((p, i) => <PostCard key={p.id} post={p} index={i} onCheckout={onCheckout} />)}
    </div>
  ) : (
    <p className="py-16 text-center text-muted-foreground">No posts here yet.</p>
  );
